
import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';
import { RootState } from '../index';

// Define interfaces
export interface WorkflowAuditRecord {
  id: string;
  entity_type: string;
  entity_id: string;
  action: string;
  user: {
    id: string;
    username: string;
    email: string;
    first_name?: string;
    last_name?: string;
  };
  timestamp: string;
  ip_address: string;
  user_agent: string;
  before_state: Record<string, unknown>;
  after_state: Record<string, unknown>;
  metadata: Record<string, unknown>;
}

export interface WorkflowAuditTrailPaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: WorkflowAuditRecord[];
}

export interface WorkflowAuditTrailFilterParams {
  entity_type?: string;
  entity_id?: string;
  action?: string;
  user?: string;
  date_from?: string;
  date_to?: string;
  ip_address?: string;
  search?: string;
  page?: number;
  page_size?: number;
}

export interface ComplianceReportParams {
  report_type: 'workflow_activity' | 'approval_history' | 'user_activity' | 'system_changes';
  entity_type?: string;
  entity_id?: string;
  date_from?: string;
  date_to?: string;
  format: 'pdf' | 'csv' | 'excel';
  include_metadata?: boolean;
}

// Create API
export const workflowAuditTrailApi = createApi({
  reducerPath: 'workflowAuditTrailApi',
  baseQuery: createExtendedTokenBaseQuery('workflows/'),
  tagTypes: ['WorkflowAuditTrail'],
  endpoints: (builder) => ({
    getAuditTrail: builder.query<WorkflowAuditTrailPaginatedResponse, WorkflowAuditTrailFilterParams | void>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined) {
              queryParams.append(key, value.toString());
            }
          });
        }
        return { url: 'audit-trail/', params: queryParams };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: 'WorkflowAuditTrail' as const, id })),
              { type: 'WorkflowAuditTrail' as const, id: 'LIST' },
            ]
          : [{ type: 'WorkflowAuditTrail' as const, id: 'LIST' }],
    }),
    
    getEntityAuditTrail: builder.query<
      WorkflowAuditTrailPaginatedResponse,
      { entity_type: string; entity_id: string; page?: number; page_size?: number }
    >({
      query: ({ entity_type, entity_id, page, page_size }) => {
        const queryParams = new URLSearchParams();
        if (page) queryParams.append('page', page.toString());
        if (page_size) queryParams.append('page_size', page_size.toString());
        
        return {
          url: `audit-trail/${entity_type}/${entity_id}/`,
          params: queryParams,
        };
      },
      providesTags: (result, error, { entity_type, entity_id }) => [
        ...(result?.results.map(({ id }) => ({ type: 'WorkflowAuditTrail' as const, id })) || []),
        { type: 'WorkflowAuditTrail' as const, id: `${entity_type}-${entity_id}` },
      ],
    }),
    
    getAuditRecordById: builder.query<WorkflowAuditRecord, string>({
      query: (id) => `audit-trail/records/${id}/`,
      providesTags: (result, error, id) => [{ type: 'WorkflowAuditTrail' as const, id }],
    }),
    
    generateComplianceReport: builder.mutation<{ report_url: string }, ComplianceReportParams>({
      query: (params) => ({
        url: 'audit-trail/reports/',
        method: 'POST',
        body: params,
      }),
      invalidatesTags: [],
    }),
    
    getAuditTrailAccessLog: builder.query<WorkflowAuditTrailPaginatedResponse, { page?: number; page_size?: number }>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.page_size) queryParams.append('page_size', params.page_size.toString());
        
        return {
          url: 'audit-trail/access-log/',
          params: queryParams,
        };
      },
      providesTags: [{ type: 'WorkflowAuditTrail' as const, id: 'ACCESS_LOG' }],
    }),
    
    getAuditTrailStats: builder.query<
      {
        total_records: number;
        activity_by_entity: Record<string, number>;
        activity_by_user: Record<string, number>;
        activity_by_action: Record<string, number>;
        activity_over_time: Array<{ date: string; count: number }>;
      },
      { date_from?: string; date_to?: string }
    >({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.date_from) queryParams.append('date_from', params.date_from);
        if (params.date_to) queryParams.append('date_to', params.date_to);
        
        return {
          url: 'audit-trail/stats/',
          params: queryParams,
        };
      },
      providesTags: [{ type: 'WorkflowAuditTrail' as const, id: 'STATS' }],
    }),
  }),
});

// Export hooks
export const {
  useGetAuditTrailQuery,
  useGetEntityAuditTrailQuery,
  useGetAuditRecordByIdQuery,
  useGenerateComplianceReportMutation,
  useGetAuditTrailAccessLogQuery,
  useGetAuditTrailStatsQuery,
} = workflowAuditTrailApi;
